/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The strongest claim that holds for EVERY active device in this response — not merely the org's matching mode. "device": every active device was matched on its own hardware serial, so each one ran the agent. "user": at least one active device was matched only on its assigned-user email, so the set as a whole supports only the weaker claim that assigned users ran the agent somewhere.
 */
export const Attestation = {
  Device: "device",
  User: "user",
} as const;
/**
 * The strongest claim that holds for EVERY active device in this response — not merely the org's matching mode. "device": every active device was matched on its own hardware serial, so each one ran the agent. "user": at least one active device was matched only on its assigned-user email, so the set as a whole supports only the weaker claim that assigned users ran the agent somewhere.
 */
export type Attestation = OpenEnum<typeof Attestation>;

/**
 * Agent-coverage summary across the org's connected MDM inventories. What a bucket attests depends on the org's matching mode: under device-level matching (hardware serial) agent_active means THIS machine reported in, while under user-level matching it means only that the device's assigned user has a fresh heartbeat somewhere.
 */
export type DeviceIntegrationCoverage = {
  /**
   * Freshness window: an agent counts as active when its heartbeat is within this many minutes.
   */
  activeWindowMinutes: number;
  /**
   * Devices with an agent heartbeat or state report within the window. What that attests depends on the matching mode and, per device, on whether the match was by serial or by email — compare against agent_active_device_attested.
   */
  agentActive: number;
  /**
   * How many of agent_active are backed by that machine's OWN heartbeat or state report (serial match). Under user-level matching this is 0; under device-level matching a value below agent_active means some devices fell back to the assigned-user email, which is the weaker claim.
   */
  agentActiveDeviceAttested: number;
  /**
   * Device-level matching only: devices whose assigned user runs the agent, but not on this machine. Always 0 under user-level matching, which cannot distinguish this from agent_active.
   */
  agentOtherDevice: number;
  /**
   * Devices with a known agent that went quiet — the drift/disable case.
   */
  agentStale: number;
  /**
   * The strongest claim that holds for EVERY active device in this response — not merely the org's matching mode. "device": every active device was matched on its own hardware serial, so each one ran the agent. "user": at least one active device was matched only on its assigned-user email, so the set as a whole supports only the weaker claim that assigned users ran the agent somewhere.
   */
  attestation: Attestation;
  /**
   * agent_active devices whose latest state report says the kill switch halted enforcement, because the reporting agent's build was blocked when it reported.
   */
  enforcementHalted: number;
  /**
   * Devices absent from the latest completed MDM snapshot.
   */
  missing: number;
  /**
   * Devices whose assigned email resolves to an org member with no agent at all.
   */
  noAgent: number;
  /**
   * Devices the MDM reports with no assigned-user email.
   */
  noEmail: number;
  /**
   * Organization the coverage describes.
   */
  organizationId: string;
  /**
   * agent_active devices whose latest report applies the policy last served to the reporting user.
   */
  policyAttested: number;
  /**
   * agent_active devices that report a different policy, or none.
   */
  policyOutdated: number;
  /**
   * agent_active devices with no state report within the window, typically an agent that predates reporting.
   */
  policyUnreported: number;
  /**
   * All synced devices, including missing ones.
   */
  totalDevices: number;
  /**
   * Agent users with no matching device in any connected MDM inventory — shadow devices or fleets outside MDM.
   */
  unmanagedAgentUsers: number;
  /**
   * Devices whose assigned email matches neither an agent user nor an org member.
   */
  unresolvedEmail: number;
};

/** @internal */
export const Attestation$inboundSchema: z.ZodMiniType<Attestation, unknown> =
  openEnums.inboundSchema(Attestation);

/** @internal */
export const DeviceIntegrationCoverage$inboundSchema: z.ZodMiniType<
  DeviceIntegrationCoverage,
  unknown
> = z.pipe(
  z.object({
    active_window_minutes: z.int(),
    agent_active: z.int(),
    agent_active_device_attested: z.int(),
    agent_other_device: z.int(),
    agent_stale: z.int(),
    attestation: Attestation$inboundSchema,
    enforcement_halted: z.int(),
    missing: z.int(),
    no_agent: z.int(),
    no_email: z.int(),
    organization_id: z.string(),
    policy_attested: z.int(),
    policy_outdated: z.int(),
    policy_unreported: z.int(),
    total_devices: z.int(),
    unmanaged_agent_users: z.int(),
    unresolved_email: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "active_window_minutes": "activeWindowMinutes",
      "agent_active": "agentActive",
      "agent_active_device_attested": "agentActiveDeviceAttested",
      "agent_other_device": "agentOtherDevice",
      "agent_stale": "agentStale",
      "enforcement_halted": "enforcementHalted",
      "no_agent": "noAgent",
      "no_email": "noEmail",
      "organization_id": "organizationId",
      "policy_attested": "policyAttested",
      "policy_outdated": "policyOutdated",
      "policy_unreported": "policyUnreported",
      "total_devices": "totalDevices",
      "unmanaged_agent_users": "unmanagedAgentUsers",
      "unresolved_email": "unresolvedEmail",
    });
  }),
);

export function deviceIntegrationCoverageFromJSON(
  jsonString: string,
): SafeParseResult<DeviceIntegrationCoverage, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DeviceIntegrationCoverage$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DeviceIntegrationCoverage' from JSON`,
  );
}
